import React, { useState, SyntheticEvent, useMemo, useEffect } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';

import moment, { Moment } from 'moment';
import classnames from 'classnames';
import _debounce from 'lodash/debounce';

import { Participation, useUpdateParticipationNotes } from '@app/api';

import { TextArea, useToast } from 'components';
import { CircularProgress } from '@mui/material';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    paddingLeft: theme.marginBase * 4,
    paddingRight: theme.marginBase * 4,
    paddingTop: theme.marginBase * 4,
    flex: 1,
    overflow: 'auto',
  },
  textarea: {
    border: 'none',
    width: '100%',
    height: '100%',
    resize: 'vertical',
  },
  saveStatus: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    borderTop: `1px solid ${theme.colors.grayBorder}`,
    marginLeft: theme.marginBase * 4,
    marginRight: theme.marginBase * 4,
    paddingTop: theme.marginBase * 2,
    paddingBottom: theme.marginBase * 2,
  },
  icon: {
    fontSize: theme.iconSize.regular,
    color: theme.colors.blue,
    marginRight: theme.marginBase,
  },
}));

interface Props {
  participation: Participation;
}

export const ParticipationNotes = ({ participation }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const toast = useToast();

  const [notes, setNotes] = useState(participation.notes);

  const [saveTime, setSaveTime] = useState<Moment | null>(null);
  const [intervalTime, setIntervalTime] = useState<Moment>(moment());

  const { mutate: saveNotes, isLoading: loading } = useUpdateParticipationNotes(
    participation.id,
    {
      onSuccess: () => setSaveTime(moment()),
      onError: toast.exception,
    },
  );

  useEffect(() => {
    const interval = setInterval(() => setIntervalTime(moment()), 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return setNotes(participation.notes ? participation.notes : '');
    // eslint-disable-next-line
  }, [setNotes, participation.id]);

  const _saveNotes = useMemo(
    () =>
      _debounce(saveNotes, 500, {
        trailing: true,
      }),
    [saveNotes],
  );

  const handleChange = (e: SyntheticEvent<HTMLTextAreaElement>) => {
    setNotes(e.currentTarget.value);
    _saveNotes({ notes: e.currentTarget.value });
  };

  return (
    <>
      <div className={classes.container}>
        <TextArea
          placeholder={t('sessions.details.notes.placeholder')}
          className={classes.textarea}
          onChange={handleChange}
          value={notes}
          rows={20}
          noMargin
          autoFocus
        />
      </div>
      <div className={classes.saveStatus}>
        {loading ? (
          <>
            <span className={classes.icon}>
              <CircularProgress size={15} />
            </span>
            {t('sessions.details.notes.loading')}
          </>
        ) : (
          saveTime &&
          intervalTime && (
            <>
              <i className={classnames(classes.icon, 'ri-check-line')} />
              {t('sessions.details.notes.saveStatus', {
                time:
                  saveTime.diff(intervalTime, 'minutes') > 1
                    ? saveTime.from(intervalTime)
                    : saveTime.fromNow(),
              })}
            </>
          )
        )}
      </div>
    </>
  );
};
